import { useEffect, useState } from "react";
import { Layout } from "@/components/Layout";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { User, MapPin, Globe, Coins, Bell, Moon, Sun, Trash2, Plus, Settings as SettingsIcon, Truck } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { useTheme } from "@/context/ThemeContext";
import { useCurrency } from "@/context/CurrencyContext";

const emptyAddress = { label: "Home", full_name: "", phone: "", line1: "", city: "", country: "Nigeria", postal_code: "" };

export default function Settings() {
  const { user, refresh } = useAuth();
  const { t, lang, setLang, langs } = useI18n();
  const { theme, toggle } = useTheme();
  const { currency, setCurrency, currencies } = useCurrency();

  const [profile, setProfile] = useState({ name: "", city: "" });
  const [addresses, setAddresses] = useState([]);
  const [newAddr, setNewAddr] = useState(emptyAddress);
  const [regions, setRegions] = useState([]);
  const [shipTo, setShipTo] = useState("");
  const [notif, setNotif] = useState({ orders: true, chat: true, promos: true, email: true, sms: false });

  useEffect(() => {
    if (user) {
      setProfile({ name: user.name || "", city: user.city || "" });
      setShipTo(user.ship_to || "");
      if (user.notifications) setNotif((n) => ({ ...n, ...user.notifications }));
    }
    api.get("/addresses").then((r) => setAddresses(r.data)).catch(() => {});
    api.get("/regions").then((r) => setRegions(r.data)).catch(() => {});
  }, [user]);

  const saveProfile = async () => {
    try { await api.put("/me/profile", profile); await refresh(); toast.success("Profile updated"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const addAddress = async () => {
    if (!newAddr.full_name || !newAddr.line1) return toast.error("Enter name and address");
    try { const { data } = await api.post("/addresses", newAddr); setAddresses(data); setNewAddr(emptyAddress); toast.success("Address added"); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };
  const delAddress = async (id) => {
    try { const { data } = await api.delete(`/addresses/${id}`); setAddresses(data); toast.success("Removed"); }
    catch { toast.error("Failed"); }
  };
  const saveShipTo = async (val) => {
    setShipTo(val);
    try { await api.put("/me/settings", { ship_to: val }); await refresh(); toast.success("Ship-to region saved"); }
    catch { toast.error("Failed"); }
  };
  const saveNotif = async (next) => {
    setNotif(next);
    try { await api.put("/me/settings", { notifications: next }); toast.success("Notifications saved"); }
    catch { toast.error("Failed"); }
  };
  const saveLang = async (l) => { setLang(l); api.put("/me/settings", { language: l }).catch(() => {}); };

  return (
    <Layout hideAd>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <h1 className="font-display font-bold text-3xl mb-1 flex items-center gap-2"><SettingsIcon className="w-7 h-7 text-primary" />{t("settings") || "Settings"}</h1>
        <p className="text-muted-foreground mb-6">Manage your profile, addresses, currency, language and preferences.</p>

        <Tabs defaultValue="profile">
          <TabsList className="flex-wrap h-auto">
            <TabsTrigger value="profile" data-testid="settings-tab-profile"><User className="w-3.5 h-3.5 mr-1" />Profile</TabsTrigger>
            <TabsTrigger value="addresses" data-testid="settings-tab-addresses"><MapPin className="w-3.5 h-3.5 mr-1" />Addresses</TabsTrigger>
            <TabsTrigger value="preferences" data-testid="settings-tab-preferences"><Coins className="w-3.5 h-3.5 mr-1" />Currency & Language</TabsTrigger>
            <TabsTrigger value="notifications" data-testid="settings-tab-notifications"><Bell className="w-3.5 h-3.5 mr-1" />Notifications</TabsTrigger>
            <TabsTrigger value="general" data-testid="settings-tab-general"><SettingsIcon className="w-3.5 h-3.5 mr-1" />General</TabsTrigger>
          </TabsList>

          <TabsContent value="profile" className="mt-6">
            <Card className="p-5 space-y-4 max-w-lg">
              <div><Label>Full name</Label><Input data-testid="settings-name" value={profile.name} onChange={(e) => setProfile({ ...profile, name: e.target.value })} className="mt-1" /></div>
              <div><Label>City</Label><Input data-testid="settings-city" value={profile.city} onChange={(e) => setProfile({ ...profile, city: e.target.value })} className="mt-1" /></div>
              <div><Label>Email</Label><Input value={user?.email || ""} disabled className="mt-1 opacity-70" /></div>
              <Button data-testid="settings-save-profile" onClick={saveProfile} className="rounded-full">Save profile</Button>
            </Card>
          </TabsContent>

          <TabsContent value="addresses" className="mt-6 space-y-4">
            <Card className="p-5">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Truck className="w-4 h-4" />Ship to region</h3>
              <Select value={shipTo} onValueChange={saveShipTo}>
                <SelectTrigger data-testid="settings-ship-to" className="max-w-xs"><SelectValue placeholder="Select your region" /></SelectTrigger>
                <SelectContent>{regions.map((r) => <SelectItem key={r.code} value={r.country}>{r.country}</SelectItem>)}</SelectContent>
              </Select>
            </Card>
            <Card className="p-5">
              <h3 className="font-semibold mb-3">Saved shipping addresses</h3>
              <div className="space-y-2 mb-4">
                {addresses.map((a) => (
                  <div key={a.address_id} data-testid={`address-${a.address_id}`} className="flex items-start justify-between gap-3 p-3 rounded-lg border">
                    <div className="text-sm"><p className="font-medium">{a.label} · {a.full_name}</p><p className="text-muted-foreground">{a.line1}, {a.city}, {a.country} {a.postal_code}</p><p className="text-muted-foreground">{a.phone}</p></div>
                    <button data-testid={`del-address-${a.address_id}`} onClick={() => delAddress(a.address_id)}><Trash2 className="w-4 h-4 text-muted-foreground hover:text-destructive" /></button>
                  </div>
                ))}
                {addresses.length === 0 && <p className="text-sm text-muted-foreground">No addresses saved yet.</p>}
              </div>
              <div className="grid sm:grid-cols-2 gap-2 border-t pt-4">
                <Input placeholder="Label (Home/Work)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} />
                <Input data-testid="addr-fullname" placeholder="Full name" value={newAddr.full_name} onChange={(e) => setNewAddr({ ...newAddr, full_name: e.target.value })} />
                <Input placeholder="Phone" value={newAddr.phone} onChange={(e) => setNewAddr({ ...newAddr, phone: e.target.value })} />
                <Input data-testid="addr-line1" placeholder="Address line" value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} />
                <Input placeholder="City" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} />
                <Select value={newAddr.country} onValueChange={(v) => setNewAddr({ ...newAddr, country: v })}>
                  <SelectTrigger><SelectValue placeholder="Country" /></SelectTrigger>
                  <SelectContent>{regions.map((r) => <SelectItem key={r.code} value={r.country}>{r.country}</SelectItem>)}</SelectContent>
                </Select>
                <Input placeholder="Postal code" value={newAddr.postal_code} onChange={(e) => setNewAddr({ ...newAddr, postal_code: e.target.value })} />
              </div>
              <Button data-testid="settings-add-address" onClick={addAddress} className="rounded-full mt-3"><Plus className="w-4 h-4 mr-1" />Add address</Button>
            </Card>
          </TabsContent>

          <TabsContent value="preferences" className="mt-6 space-y-4">
            <Card className="p-5 max-w-lg">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Coins className="w-4 h-4" />Currency</h3>
              <Select value={currency} onValueChange={setCurrency}>
                <SelectTrigger data-testid="settings-currency"><SelectValue /></SelectTrigger>
                <SelectContent>{currencies.map((c) => <SelectItem key={c.code} value={c.code}>{c.symbol} {c.name} ({c.code})</SelectItem>)}</SelectContent>
              </Select>
              <p className="text-xs text-muted-foreground mt-2">Prices display in your chosen currency using live exchange rates. Card checkout charges in EUR or USD; Naira (₦) checkout activates once Paystack is enabled.</p>
            </Card>
            <Card className="p-5 max-w-lg">
              <h3 className="font-semibold mb-3 flex items-center gap-2"><Globe className="w-4 h-4" />Language</h3>
              <Select value={lang} onValueChange={saveLang}>
                <SelectTrigger data-testid="settings-language"><SelectValue /></SelectTrigger>
                <SelectContent>{langs.map((l) => <SelectItem key={l.code} value={l.code}>{l.flag} {l.label}</SelectItem>)}</SelectContent>
              </Select>
            </Card>
          </TabsContent>

          <TabsContent value="notifications" className="mt-6">
            <Card className="p-5 max-w-lg space-y-4">
              {[["orders", "Order updates"], ["chat", "New chat messages"], ["promos", "Promotions & deals"], ["email", "Email notifications"], ["sms", "SMS notifications"]].map(([k, label]) => (
                <div key={k} className="flex items-center justify-between">
                  <Label htmlFor={`notif-${k}`}>{label}</Label>
                  <Switch id={`notif-${k}`} data-testid={`notif-${k}`} checked={!!notif[k]} onCheckedChange={(v) => saveNotif({ ...notif, [k]: v })} />
                </div>
              ))}
            </Card>
          </TabsContent>

          <TabsContent value="general" className="mt-6">
            <Card className="p-5 max-w-lg space-y-4">
              <div className="flex items-center justify-between">
                <Label className="flex items-center gap-2">{theme === "dark" ? <Moon className="w-4 h-4" /> : <Sun className="w-4 h-4" />}Dark mode</Label>
                <Switch data-testid="settings-dark-mode" checked={theme === "dark"} onCheckedChange={toggle} />
              </div>
              <p className="text-xs text-muted-foreground">Account role: <span className="capitalize font-medium">{user?.role}</span></p>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </Layout>
  );
}
